import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/auth';
import { useOrgRole } from '../../hooks/useOrgRole';
import './Sidebar.css';

const NAV = [
  { path: '/',             icon: '⬡',  label: 'Dashboard',      cap: 'dashboard' },
  { path: '/deployments',  icon: '🚀',  label: 'Deployments',    cap: 'opsView' },
  { path: '/clouds',       icon: '☁',  label: 'Clouds',         cap: 'opsView' },
  { path: '/costs',        icon: '💰',  label: 'Costs',          cap: 'costView' },
  { path: '/invoice',      icon: '🧾',  label: 'Invoice',        cap: 'costView' },
  { path: '/optimizer',    icon: '⚡',  label: 'Optimizer',      cap: 'costView' },
  { path: '/servers',      icon: '🖥️',  label: 'Servers',        cap: 'opsView' },
  { path: '/server-groups', icon: '🔗',  label: 'Server Groups', cap: 'opsView' },
  { path: '/monitor',      icon: '📡',  label: 'Monitor',        cap: 'monitorView' },
  { path: '/integrations', icon: '🔌',  label: 'Integrations',   cap: 'opsView' },
  { path: '/settings',     icon: '⚙',  label: 'Settings',       cap: null      },
];

const PROVIDER_COLORS = {
  aws:'#ff9900',gcp:'#4285f4',azure:'#0078d4',
  digitalocean:'#0080ff',vultr:'#007bfc',lightnode:'#00c853'
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { can, role } = useOrgRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth <= 1023);
  const [clouds, setClouds] = useState([]);
  const sidebarRef = useRef(null);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 767);
      setIsTablet(w >= 768 && w <= 1023);
      if (w > 767) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const handler = e => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [mobileOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    const token = localStorage.getItem('mcp_token');
    if (!token) return;
    fetch('/api/clouds', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setClouds(d.data); }).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const showLabels = isMobile ? true : !collapsed && !isTablet;
  const visibleNav = NAV.filter(item => !item.cap || can(item.cap));

  return (
    <>
      {isMobile && (
        <div className="mobile-topbar">
          <div className="mobile-logo"><span>⬡</span> CloudUnify</div>
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Open menu">☰</button>
        </div>
      )}
      {isMobile && <div className={`sidebar-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />}
      <aside ref={sidebarRef} className={`sidebar ${collapsed && !isMobile ? 'collapsed' : ''} ${isMobile && mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-logo">
            <span className="logo-icon">⬡</span>
            {showLabels && <span className="logo-text">CloudUnify</span>}
          </div>
          {!isMobile && <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>{collapsed ? '›' : '‹'}</button>}
          {isMobile && <button className="collapse-btn" onClick={() => setMobileOpen(false)} aria-label="Close">✕</button>}
        </div>
        {showLabels && (
          <div className="sidebar-user">
            <div className="user-avatar">{user?.name?.[0]?.toUpperCase() || 'U'}</div>
            <div className="user-info">
              <div className="user-name">{user?.name || 'User'}</div>
              <div className="user-plan">{role ? `${role} role` : `${user?.plan || 'free'} plan`}</div>
            </div>
          </div>
        )}
        <nav className="sidebar-nav">
          {visibleNav.map(item => (
            <NavLink key={item.path} to={item.path} end={item.path === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title={!showLabels ? item.label : ''}>
              <span className="nav-icon">{item.icon}</span>
              {showLabels && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>
        {showLabels && (
          <div className="sidebar-section">
            <div className="section-title">Cloud Status</div>
            {clouds.filter(p => p.isConnected).length === 0
              ? <div style={{fontSize:12,color:'#4d6080',padding:'6px 4px'}}>No clouds connected</div>
              : clouds.filter(p => p.isConnected).map(p => (
                  <div key={p.provider} className="cloud-status-item">
                    <span className="cloud-dot" style={{background:PROVIDER_COLORS[p.provider]||'#888'}} />
                    <span className="cloud-name">{p.provider==='aws'?'AWS':p.provider==='gcp'?'GCP':p.provider==='azure'?'Azure':p.provider==='digitalocean'?'DO':p.provider.charAt(0).toUpperCase()+p.provider.slice(1)}</span>
                    <span className="cloud-badge connected">ON</span>
                  </div>
                ))
            }
          </div>
        )}
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span>⏻</span>
            {showLabels && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
